import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import LogoHpa from './images/logo.png';
import './stock.css';
import { loginUser } from '../utils/auth';

const MgStock = ({ socket }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [medicaments, setMedicaments] = useState([]);
  const [ClientsEnAttente, setClientsEnAttente] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMedicaments, setSelectedMedicaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [socketConnected, setSocketConnected] = useState(false);
  const [formData, setFormData] = useState({
    motif: '',
    observations: '',
    stockn: '',
    numAirTel: '',
    numID_LNE: ''
  });

  const errors = {
    uname: "Utilisateur Inconnu",
    pass: "Erreur sur Code d'accès"
  };

  // Vérifier la connexion socket et identifier le module
  useEffect(() => {
    if (socket) {
      console.log('🔌 Stock - Socket initialisé');

      const handleConnect = () => {
        console.log('✅ Stock - Socket connecté');
        setSocketConnected(true);
        socket.emit('module_connected', { module: 'Stock', socketId: socket.id });
        chargerMedicaments();
        chargerClientsEnAttente();
      };

      const handleDisconnect = () => {
        console.log('❌ Stock - Socket déconnecté');
        setSocketConnected(false);
      };

      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('medicaments_updated', (data) => {
        console.log('🔄 Mise à jour des médicaments reçue');
        setMedicaments(data);
      });
      
      // Écouter les nouveaux Clients en attente à la stock
      socket.on('nouveau_client_stock', (data) => {
        console.log('🆕 Nouveau Client à la stock reçu:', data);
        setClientsEnAttente(prev => {
          const newList = [...prev, data];
          // Trier par date d'arrivée (le plus ancien d'abord)
          newList.sort((a, b) => new Date(a.dateArrivee) - new Date(b.dateArrivee));
          return newList;
        });
      });

      // Écouter les mises à jour de la file d'attente
      socket.on('attente_stock_updated', (data) => {
        console.log('🔄 File d\'attente stock mise à jour:', data);
        setClientsEnAttente(data);
      });

      // Écouter les prises en charge terminées
      socket.on('prise_en_charge_stock_terminée', (data) => {
        console.log('✅ Prise en charge stock terminée:', data);
        chargerClientsEnAttente();
        chargerMedicaments();
      });

      if (socket.connected) {
        setSocketConnected(true);
        socket.emit('module_connected', { module: 'Stock', socketId: socket.id });
        chargerMedicaments();
        chargerClientsEnAttente();
      }

      return () => {
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('medicaments_updated');
        socket.off('nouveau_client_stock');
        socket.off('attente_stock_updated');
        socket.off('prise_en_charge_stock_terminée');
      };
    }
  }, [socket]);

  // Charger les médicaments
  const chargerMedicaments = () => {
    if (!socket || !socket.connected) return;
    setLoading(true);
    socket.emit('load_medicaments', (response) => {
      setLoading(false);
      if (response && response.success) {
        setMedicaments(response.data || []);
        console.log('✅ Médicaments chargés:', response.data?.length);
      }
    });
  };

  // Charger les Clients en attente à la stock
  const chargerClientsEnAttente = () => {
    if (!socket || !socket.connected) {
      console.log('❌ Socket non connecté pour charger les Clients en attente');
      return;
    }
    
    console.log('👥 Chargement des Clients en attente à la stock...');
    socket.emit('load_clients_attente_stock', (response) => {
      if (response && response.success) {
        const sorted = (response.data || []).sort((a, b) => new Date(a.dateArrivee) - new Date(b.dateArrivee));
        setClientsEnAttente(sorted);
        console.log('✅ Clients en attente à la stock:', sorted.length);
      } else {
        console.error('❌ Erreur chargement Clients attente:', response?.message);
      }
    });
  };

  // Rechercher un médicament
  const rechercherMedicament = (term) => {
    setSearchTerm(term);
    if (term.length < 2) {
      chargerMedicaments();
      return;
    }
    if (!socket || !socket.connected) return;
    socket.emit('search_medicament', { term }, (response) => {
      if (response && response.success) setMedicaments(response.data);
    });
  };

  // Démarrer une prise en charge
  const demarrerPriseEnCharge = (Client) => {
    console.log('💊 Démarrage prise en charge pour:', Client.nomClient);
    setSelectedClient(Client);
    setSelectedMedicaments(Client.medicaments || []);
    setFormData({
      motif: Client.motif || '',
      observations: '',
      stockn: localStorage.getItem('stockn') || '',
      numAirTel: Client.numAirTel || '',
      numID_LNE: Client.numID_LNE || ''
    });
  };

  // Ajouter un médicament à la sélection
  const ajouterMedicament = (medicament) => {
    if (medicament.stock <= 0) {
      alert("Ce médicament est en rupture de stock");
      return;
    }
    setSelectedMedicaments(prev => {
      const existing = prev.find(m => m.id === medicament.id);
      if (existing) {
        if (existing.quantite + 1 > medicament.stock) {
          alert(`Stock insuffisant. Il reste ${medicament.stock} unités.`);
          return prev;
        }
        return prev.map(m => 
          m.id === medicament.id 
            ? { ...m, quantite: m.quantite + 1, total: (m.quantite + 1) * m.prix }
            : m
        );
      }
      return [...prev, { ...medicament, quantite: 1, total: medicament.prix }];
    });
  };

  // Modifier la quantité
  const modifierQuantite = (id, delta) => {
    setSelectedMedicaments(prev => {
      return prev.map(m => {
        if (m.id === id) {
          const nouvelleQuantite = Math.max(1, m.quantite + delta);
          if (nouvelleQuantite > m.stock) {
            alert(`Stock insuffisant. Il reste ${m.stock} unités.`);
            return m;
          }
          return { ...m, quantite: nouvelleQuantite, total: nouvelleQuantite * m.prix };
        }
        return m;
      });
    });
  };

  // Retirer un médicament
  const retirerMedicament = (id) => {
    setSelectedMedicaments(prev => prev.filter(m => m.id !== id));
  };

  // Calculer le total
  const calculerTotal = () => {
    return selectedMedicaments.reduce((sum, m) => sum + m.total, 0);
  };

  // Terminer la prise en charge
  const terminerPriseEnCharge = (e) => {
    e.preventDefault();
    
    if (selectedMedicaments.length === 0) {
      alert("Veuillez sélectionner au moins un médicament");
      return;
    }

    if (!socket || !socket.connected) {
      alert("Le serveur est indisponible. Vérifiez la connexion Socket.IO.");
      return;
    }

    setLoading(true);
    
    const montantTotal = calculerTotal();
    
    const stockData = {
      ...selectedClient,
      ...formData,
      medicaments: selectedMedicaments,
      montantTotal: montantTotal,
      datePriseEnCharge: new Date().toISOString(),
      status: "terminé"
    };

    console.log('📤 Envoi de la prise en charge terminée:', stockData);
    
    socket.emit("terminer_prise_en_charge_stock", stockData, (response) => {
      setLoading(false);
      if (response && response.success) {
        alert(`✅ Prise en charge terminée avec succès!\nTotal: ${montantTotal.toLocaleString()} FCFA`);
        setSelectedClient(null);
        setSelectedMedicaments([]);
        chargerMedicaments();
        chargerClientsEnAttente();
      } else {
        console.error('❌ Erreur terminer prise en charge stock:', response?.message);
        alert("Erreur: " + (response?.message || "Erreur inconnue"));
      }
    });
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const uname = event.target.uname.value;
    const pass = event.target.pass.value;

    const result = await loginUser(uname, pass);
    if (result.success) {
      setIsAuthenticated(true);
      localStorage.setItem('stockn', uname);
    } else {
      setErrorMessages({ name: "pass", message: result.message || errors.pass });
    }
  };

  const renderErrorMessage = (name) =>
    name === errorMessages.name && (
      <div className="message_erreur">{errorMessages.message}</div>
    );

  const renderForm = (
    <div className="form">
      <form className='form_margin BC' onSubmit={handleLogin}>
        <br />
        <br />
        <label className='sous_titre'>Utilisateur </label>
        <input className='utilisateur_interne' minLength={4} type="text" name="uname" required />
        {renderErrorMessage("uname")}
        <label className='sous_titre'>Code d'accès</label>
        <input className='utilisateur_interne' minLength={4} type="password" name="pass" required />
        {renderErrorMessage("pass")}
        <div className='ftt__footer BC'>
          <button className='home__cta'>Connexion</button>
        </div>
      </form>
    </div>
  );

  const sirAcceuil = () => navigate("/PageAcceuil");

  if (!isAuthenticated) return renderForm;

  // Filtrer les médicaments par recherche
  const medicamentsFiltres = searchTerm 
    ? medicaments.filter(m => m.nom.toLowerCase().includes(searchTerm.toLowerCase()))
    : medicaments;

  return (
    <>
      <div className="entete TC">
        <div className="marges_logo_5px">
          <img className='logo_clinique' src={LogoHpa} alt="LNE" />
        </div>
        <div className='titre_entete'>
          <h2 className='titre_entete'>LNE - Ndjamena</h2>
          <h3 className='sous_titre_entete'>Service de Stock</h3>
        </div>
      </div>

      <div className="socket-info">
        <p>
          🔌 Serveur {socketConnected ? '✅ Connecté' : '❌ Déconnecté'} | 
          💊 {ClientsEnAttente.length} Client(s) en attente | 
          📦 {medicaments.length} médicaments en stock
        </p>
      </div>

      <div className="stock-container">
        {!selectedClient ? (
          <div className="stock-grid">
            <div className="file-attente">
              <h3>💊 Clients en attente ({ClientsEnAttente.length})</h3>
              <div className="Clients-list">
                {ClientsEnAttente.length === 0 ? (
                  <div className="aucun-Client">
                    <p>Aucun Client en attente</p>
                  </div>
                ) : (
                  ClientsEnAttente.map(Client => (
                    <div 
                      key={Client.id || Client.numClient} 
                      className="Client-attente-card"
                    >
                      <div className="Client-info">
                        <h4>{Client.nomClient}</h4>
                        <div className="Client-badges">
                          <span className="heure-arrivee">
                            Arrivée: {new Date(Client.dateArrivee).toLocaleTimeString()}
                          </span>
                        </div>
                        <p>LNE: {Client.numID_LNE || 'Non renseigné'}</p>
                        <p>Tél: {Client.numAirTel}</p>
                        <p className="medicaments-list-preview">
                          💊 {Client.medicaments?.length || 0} médicament(s)
                        </p>
                        {Client.medecinPrescripteur && (
                          <p className="medecin">👨‍⚕️ Prescrit par: {Client.medecinPrescripteur}</p>
                        )}
                      </div>
                      <button 
                        className="btn-demarrer-prise-en-charge"
                        onClick={() => demarrerPriseEnCharge(Client)}
                      >
                        Traiter la prescription
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="medicaments-catalogue">
              <h3>📋 Catalogue des Médicaments</h3>
              <div className="recherche-bar">
                <input
                  type="text"
                  placeholder="🔍 Rechercher un médicament..."
                  value={searchTerm}
                  onChange={(e) => rechercherMedicament(e.target.value)}
                  className="recherche-input"
                />
              </div>
              <div className="medicaments-list">
                {loading ? (
                  <div className="loading-message">Chargement...</div>
                ) : (
                  <div className="medicaments-grid">
                    {medicamentsFiltres.map(med => (
                      <div key={med.id} className="medicament-card">
                        <div className="medicament-info">
                          <h4>{med.nom}</h4>
                          <p className="medicament-prix">{med.prix.toLocaleString()} FCFA</p>
                          <p className={`medicament-stock ${med.stock <= 10 ? 'stock-faible' : ''}`}>
                            Stock: {med.stock} unités
                          </p>
                          {med.ordonnance && <span className="ordonnance-badge">🔴 Sur Ordonnance</span>}
                        </div>
                      </div>
                    ))}
                    {medicamentsFiltres.length === 0 && (
                      <div className="aucun-medicament">
                        <p>Aucun médicament trouvé</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="prise-en-charge-form-container">
            <div className="form-header">
              <h3>Prise en charge: {selectedClient.nomClient}</h3>
              <button className="btn-retour" onClick={() => setSelectedClient(null)}>
                ← Retour
              </button>
            </div>

            <div className="Client-info-banner">
              <div className="info-row">
                <span><strong>LNE ID:</strong> {selectedClient.numID_LNE || 'Non renseigné'}</span>
                <span><strong>Téléphone:</strong> {selectedClient.numAirTel}</span>
              </div>
              {selectedClient.medecinPrescripteur && (
                <div className="info-row">
                  <span><strong>Médecin prescripteur:</strong> {selectedClient.medecinPrescripteur}</span>
                  <span><strong>Date prescription:</strong> {new Date(selectedClient.datePrescription).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label>Observations</label>
                <textarea
                  value={formData.observations}
                  onChange={(e) => setFormData({...formData, observations: e.target.value})}
                  className="form-textarea"
                  rows="2"
                  placeholder="Observations particulières..."
                />
              </div>
              <div className="form-group half">
                <label>Stockn</label>
                <input
                  type="text"
                  value={formData.stockn}
                  onChange={(e) => setFormData({...formData, stockn: e.target.value})}
                  className="form-input"
                  placeholder="Nom du stockn"
                />
              </div>
            </div>

            <div className="medicaments-selection">
              <h4>Sélection des médicaments</h4>
              <div className="recherche-bar">
                <input
                  type="text"
                  placeholder="🔍 Rechercher un médicament..."
                  onChange={(e) => rechercherMedicament(e.target.value)}
                  className="recherche-input"
                />
              </div>
              <div className="medicaments-grid small">
                {medicamentsFiltres.slice(0, 6).map(med => (
                  <button
                    key={med.id}
                    className="btn-ajouter-medicament"
                    onClick={() => ajouterMedicament(med)}
                    disabled={med.stock <= 0}
                  >
                    + {med.nom} - {med.prix.toLocaleString()} FCFA
                  </button>
                ))}
              </div>
            </div>

            <div className="panier-section">
              <h4>Médicaments sélectionnés ({selectedMedicaments.length})</h4>
              <div className="panier-items">
                {selectedMedicaments.length === 0 ? (
                  <p className="panier-vide">Aucun médicament sélectionné</p>
                ) : (
                  selectedMedicaments.map(item => (
                    <div key={item.id} className="panier-item">
                      <div className="item-info">
                        <span className="item-nom">{item.nom}</span>
                        <div className="item-quantite">
                          <button onClick={() => modifierQuantite(item.id, -1)}>-</button>
                          <span>{item.quantite}</span>
                          <button onClick={() => modifierQuantite(item.id, 1)}>+</button>
                        </div>
                        <span className="item-prix">{item.total.toLocaleString()} FCFA</span>
                      </div>
                      <button className="btn-retirer" onClick={() => retirerMedicament(item.id)}>
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="panier-total">
                <strong>Total:</strong>
                <span className="total-montant">{calculerTotal().toLocaleString()} FCFA</span>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn-terminer" 
                onClick={terminerPriseEnCharge}
                disabled={loading || selectedMedicaments.length === 0}
              >
                {loading ? 'Enregistrement...' : '✅ Terminer la prise en charge'}
              </button>
              <button type="button" className="btn-annuler" onClick={() => setSelectedClient(null)}>
                ❌ Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      <div className='ftt__footer'>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
          <button className="glow-on-hover MenuBtn" type="button" onClick={sirAcceuil}>
            Retour à l'accueil
          </button>
        </div>
      </div>

      <style jsx>{`
        .stock-container {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }
        .stock-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
        }
        .file-attente, .medicaments-catalogue {
          background: white;
          border-radius: 15px;
          padding: 20px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
        }
        .file-attente h3, .medicaments-catalogue h3 {
          color: #2E7D32;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e9ecef;
        }
        .Clients-list {
          display: grid;
          grid-template-columns: 1fr;
          gap: 15px;
          max-height: 500px;
          overflow-y: auto;
        }
        .Client-attente-card {
          border-radius: 10px;
          padding: 15px;
          transition: all 0.3s ease;
          background: white;
          border: 1px solid #e9ecef;
        }
        .Client-attente-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        .Client-badges {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 8px 0;
          flex-wrap: wrap;
        }
        .heure-arrivee {
          font-size: 11px;
          color: #6c757d;
        }
        .medicaments-list-preview, .medecin {
          font-size: 12px;
          color: #495057;
          margin-top: 5px;
        }
        .btn-demarrer-prise-en-charge {
          width: 100%;
          padding: 10px;
          margin-top: 10px;
          background: linear-gradient(135deg, #4CAF50, #6f42c1);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        .recherche-bar {
          margin-bottom: 15px;
        }
        .medicaments-list {
          max-height: 400px;
          overflow-y: auto;
        }
        .medicaments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 10px;
        }
        .medicaments-grid.small {
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          max-height: 200px;
        }
        .medicament-card {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 10px;
          background: #f8f9fa;
        }
        .medicament-card h4 {
          font-size: 14px;
          margin-bottom: 5px;
        }
        .medicament-prix {
          color: #28a745;
          font-weight: bold;
          font-size: 14px;
        }
        .medicament-stock {
          font-size: 11px;
          color: #6c757d;
        }
        .stock-faible {
          color: #dc3545;
          font-weight: bold;
        }
        .ordonnance-badge {
          display: inline-block;
          background: #dc3545;
          color: white;
          font-size: 9px;
          padding: 2px 6px;
          border-radius: 10px;
        }
        .btn-ajouter-medicament {
          width: 100%;
          padding: 8px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 12px;
        }
        .prise-en-charge-form-container {
          background: white;
          border-radius: 15px;
          padding: 25px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
        }
        .form-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #e9ecef;
        }
        .btn-retour {
          padding: 8px 20px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        .Client-info-banner {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 10px;
          margin-bottom: 20px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 20px;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group.half {
          margin-bottom: 0;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #2E7D32;
        }
        .form-input, .form-textarea {
          width: 100%;
          padding: 10px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-family: inherit;
        }
        .form-textarea {
          resize: vertical;
        }
        .medicaments-selection, .panier-section {
          margin-bottom: 20px;
        }
        .medicaments-selection h4, .panier-section h4 {
          color: #2E7D32;
          margin-bottom: 10px;
        }
        .panier-items {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 10px;
          background: #f8f9fa;
        }
        .panier-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
        }
        .item-info {
          display: flex;
          align-items: center;
          gap: 15px;
          flex: 1;
          flex-wrap: wrap;
        }
        .item-nom {
          flex: 1;
          font-size: 13px;
        }
        .item-quantite {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .item-quantite button {
          width: 25px;
          height: 25px;
          border: none;
          background: #e9ecef;
          border-radius: 50%;
          cursor: pointer;
        }
        .item-prix {
          font-weight: bold;
          color: #28a745;
          min-width: 80px;
          text-align: right;
          font-size: 13px;
        }
        .btn-retirer {
          background: none;
          border: none;
          color: #dc3545;
          cursor: pointer;
          font-size: 16px;
        }
        .panier-vide {
          text-align: center;
          padding: 20px;
          color: #6c757d;
        }
        .panier-total {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #e9ecef;
          border-radius: 8px;
          margin-top: 10px;
          font-weight: bold;
        }
        .form-actions {
          display: flex;
          gap: 15px;
          margin-top: 20px;
        }
        .btn-terminer {
          flex: 1;
          padding: 12px;
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: bold;
          cursor: pointer;
        }
        .btn-annuler {
          flex: 1;
          padding: 12px;
          background: linear-gradient(135deg, #dc3545, #c82333);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: bold;
          cursor: pointer;
        }
        .aucun-Client, .aucun-medicament {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }
        @media (max-width: 768px) {
          .stock-grid {
            grid-template-columns: 1fr;
          }
          .form-row {
            grid-template-columns: 1fr;
          }
          .form-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </>
  );
};

export default MgStock;
